import { light } from '@league/shared/brand';
import { LeagueSheetPage, leagueSheetFrom } from './features/sheet/LeagueSheetPage';
import { TableSheetPage, tableSheetFrom } from './features/sheet/TableSheetPage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { LoginPage } from './features/auth/LoginPage';
import { SetPasswordPage } from './features/auth/SetPasswordPage';
import { ApiError, NETWORK_ERROR_MESSAGE, apiFetch, handleUnauthorized, isNetworkError } from './lib/api';
import { Button } from './components/ui';
import {
  DEFAULT_SIGN_IN_OPTIONS,
  getMe,
  getSignInOptions,
  type SignInOptions,
  signOut,
} from './lib/auth';
import { CapabilitiesProvider, DIRECTOR_CAPABILITIES } from './lib/capabilities';
import { AppRoutes } from './routes';
import { MembershipGate } from './features/onboarding/MembershipGate';
import { PageErrorBoundary } from './components/PageErrorBoundary';

interface Meta {
  brand: string;
  environment: string;
}

/**
 * The shell asks the API whether there is a session rather than guessing: 401 means sign in, 200
 * means you're through, and the role in the answer decides which app you land in.
 *
 * Deliberately no client-side authorization. The server already knows, and a second copy of the
 * rule in the SPA is a copy that can disagree with it. What the role decides here is navigation —
 * which rail you see — not what you may read.
 */
export function App() {
  const queryClient = useQueryClient();

  const meta = useQuery({
    queryKey: ['meta'],
    queryFn: () => apiFetch<Meta>('/api/meta'),
    staleTime: Infinity,
  });

  /*
    Re-asked when the tab comes back into view (2026-10-18): sign-in is shared by every tab, so
    signing in as somebody else in one must not leave another drawing the first person's screens.
  */
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false, refetchOnWindowFocus: true });

  /*
    A DIFFERENT PERSON, A CLEAN SLATE. When the signed-in account changes under an open tab, nothing
    fetched for the previous one is kept — their leagues, their instructors, their menu.
  */
  const actorId = me.data?.actor.id ?? null;
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (actorId && shownFor.current && shownFor.current !== actorId) {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
    }
    if (actorId) shownFor.current = actorId;
  }, [actorId, queryClient]);

  const options = useQuery({
    queryKey: ['sign-in-options'],
    queryFn: getSignInOptions,
    staleTime: Infinity,
  });

  const brand = meta.data?.brand ?? 'League';

  useEffect(() => {
    document.title = brand;
  }, [brand]);

  /**
   * The status-bar tint, set here for the same reason the colour lint rule exists: a palette change
   * should reach the phone's chrome too, and a hex typed into `index.html` is a copy nothing checks.
   */
  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', light.deep);
  }, []);

  const refresh = () => queryClient.invalidateQueries();

  /*
    SIGNING OUT LEAVES NOTHING BEHIND (2026-10-23). Re-asking for everything made every open screen's
    request fail on the way out, and those failures were still cached when she came back: signing in
    again on a trusted device, with no code screen in between, showed "Could not load your next
    steps" until the page asked again. Only `/api/me` is re-asked, which swaps the app for the
    sign-in screen; everything else fetched for her is then dropped.
  */
  /*
    AT ONCE, AND BACK TO THE START (2026-10-25). The sign-in screen shows the moment she presses Sign
    out, rather than after the server answers — a slow answer read as a button that did nothing. And
    the address goes back to the start, so whoever signs in next lands on their own home instead of
    the page the last person was on (a director signing in after a player got the player's profile).
  */
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const signOutAndForget = () => {
    setSigningOut(true);
    navigate('/', { replace: true });
    void signOut()
      .then(() => queryClient.invalidateQueries({ queryKey: ['me'] }))
      .then(() =>
        queryClient.removeQueries({
          predicate: (query) => !['me', 'meta', 'sign-in-options'].includes(String(query.queryKey[0])),
        }),
      )
      .finally(() => setSigningOut(false));
  };

  /**
   * BEING SIGNED OUT WHILE THE APP IS OPEN, which used to render a blank page beside a working menu.
   *
   * `apiFetch` calls this when any request that is not a sign-in attempt comes back refused. All it
   * does is drop the cached actor: the root below already shows the sign-in screen when `/api/me`
   * has no answer, so re-asking is the whole fix and there is no second signed-out path to keep
   * working.
   *
   * She lands on sign-in rather than the public site, because she was in the middle of something —
   * the marketing site is not where somebody whose session lapsed wants to arrive.
   */
  useEffect(() => {
    handleUnauthorized(() => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    });
    return () => handleUnauthorized(null);
  }, [queryClient]);

  /* Read before any early return — hooks cannot be called conditionally. */
  const location = useLocation();
  const setPasswordToken =
    location.pathname === '/set-password'
      ? new URLSearchParams(location.search).get('token')
      : null;

  /*
    THE TABLE'S SCORE SHEET, SCANNED (2026-10-06) — before the session check AND before waiting on
    it, like the set-password link: the key in the address is what authorises it, so it works the
    same for somebody signed in, signed out, or on a phone that has never seen this app.
  */
  /* The league's generic sheet (2026-10-15): one QR per league, she picks her table. */
  const leagueSheet = leagueSheetFrom(location.pathname);
  if (leagueSheet && !meta.isPending) {
    return <PageErrorBoundary><LeagueSheetPage brand={brand} leagueId={leagueSheet.leagueId} sheetKey={leagueSheet.key} /></PageErrorBoundary>;
  }

  const sheet = tableSheetFrom(location.pathname);
  if (sheet && !meta.isPending) {
    return <PageErrorBoundary><TableSheetPage brand={brand} sessionId={sheet.sessionId} tableNumber={sheet.tableNumber} sheetKey={sheet.key} /></PageErrorBoundary>;
  }

  /*
    LOADING ONLY THE FIRST TIME (2026-10-20). `me` is asked again whenever the tab comes back, and
    with no answer yet React Query counts that as pending — so a signed-out visitor saw "Loading…"
    for a moment and the sign-in form was rebuilt from its first step. The commissioner signed in
    with her password, switched to her mail for the code, came back, and was asked for her email and
    password again. A re-check keeps whatever is on the screen.
  */
  const meFirstLoad = me.isPending && me.dataUpdatedAt === 0 && me.errorUpdatedAt === 0;
  if (meFirstLoad || meta.isPending || options.isPending) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-ink-3">Loading…</p>
      </main>
    );
  }

  /*
    SETTING A PASSWORD COMES BEFORE THE SESSION CHECK, and that ordering is the whole fix.

    This screen used to live only in the signed-out branch, on the reasoning that whoever opens a
    link from an email has no session by definition. That is wrong often enough to matter: sessions
    last thirty days, so anybody who signed in on that browser before forgetting their password
    still has one. The signed-in app has no `/set-password` route, so the link fell through to the
    dashboard — the person is bounced somewhere that looks fine, their password is never changed,
    and nothing says so. The commissioner hit this on the first evening staging existed.

    Rendering it either way costs nothing: the token in the URL is what authorises the change, not
    the session, and better-auth cuts every existing session when a password is reset anyway.
  */
  if (setPasswordToken) {
    return (
      <SetPasswordRoute
        brand={brand}
        options={options.data ?? DEFAULT_SIGN_IN_OPTIONS}
        onDone={refresh}
        onSignedIn={refresh}
      />
    );
  }

  /*
    ONLY THE API SAYING NO IS BEING SIGNED OUT (2026-10-19). Any error used to show the sign-in
    screen, so a dropped connection or a restarting server unmounted whatever she had open and lost
    it. A failed re-check keeps the last answer and the screen she was on; the screen's own save
    reports the failure.
  */
  const refused = me.error instanceof ApiError && me.error.status >= 400 && me.error.status < 500;
  if (signingOut || refused || (!me.data && !me.error)) {
    return (
      <SignedOut
        brand={brand}
        options={options.data ?? DEFAULT_SIGN_IN_OPTIONS}
        onSignedIn={refresh}
      />
    );
  }

  if (!me.data) {
    return (
      <main role="alert" className="mx-auto flex min-h-screen max-w-xl flex-col items-start justify-center gap-4 px-6">
        <p className="font-display text-xl">{isNetworkError(me.error) ? NETWORK_ERROR_MESSAGE : 'Something went wrong.'}</p>
        <Button onClick={() => void me.refetch()} disabled={me.isFetching}>Try again</Button>
      </main>
    );
  }

  /*
    The membership gate sits ABOVE the routes, not inside them. A director who has not chosen a plan
    has no leagues, no roster and nothing to do, so the honest shape is one screen rather than a
    banner over an app that refuses everything she touches.
  */
  return (
    <MembershipGate
      actor={me.data.actor}
      brand={brand}
      onSignOut={signOutAndForget}
    >
      <CapabilitiesProvider value={{ nav: me.data.nav ?? DIRECTOR_CAPABILITIES.nav, leagueTabs: me.data.leagueTabs ?? DIRECTOR_CAPABILITIES.leagueTabs }}>
        <AppRoutes
          actor={me.data.actor}
          playsToo={me.data.playsToo ?? false}
          brand={brand}
          onSignOut={signOutAndForget}
        />
      </CapabilitiesProvider>
    </MembershipGate>
  );
}

/**
 * Signed out, and still routed.
 *
 * The set-password link in an email is opened by someone who by definition has no session, so it
 * cannot live behind the signed-in routes. Everything else collapses to the sign-in screen.
 */
function SignedOut({
  brand,
  options,
  onSignedIn,
}: {
  brand: string;
  options: SignInOptions;
  onSignedIn: () => void;
}) {
  return (
    <Routes>
      <Route
        path="/set-password"
        element={<SetPasswordRoute brand={brand} options={options} onSignedIn={onSignedIn} />}
      />
      <Route
        path="*"
        element={<LoginPage brand={brand} options={options} onSignedIn={onSignedIn} />}
      />
    </Routes>
  );
}

function SetPasswordRoute({
  brand,
  options,
  onDone,
  onSignedIn,
}: {
  brand: string;
  options: SignInOptions;
  onDone?: () => void;
  onSignedIn?: () => void;
}) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const email = params.get('email');

  if (!token) return <Navigate to="/" replace />;

  /**
   * Setting a password does NOT sign anyone in — that is better-auth's behaviour — so accepting an
   * invite used to end at a sign-in form. The flow has no such step: she accepts, gets her code and
   * chooses a plan in one run, and being asked to type in the address the email was just sent to
   * reads as having been dropped halfway.
   *
   * So when the link carries her address, this continues straight into the second factor with the
   * password she has just chosen. Somebody who merely lost their password has no address in the
   * link and still comes back through the front door, which is right — that IS a sign-in.
   *
   * Reached from `App` before the session check, so it works whether or not the browser already
   * holds one — see the note there.
   */
  return (
    <SetPasswordPage
      brand={brand}
      token={token}
      options={options}
      /*
        Back to the front door, and ASK THE SERVER AGAIN on the way. Resetting a password revokes
        every existing session, so a browser that arrived here signed in is not any more — without
        the refetch the app would keep rendering the dashboard from a session that no longer exists,
        and the next request would fail instead.
      */
      continueAs={email}
      onSignedIn={() => {
        navigate('/', { replace: true });
        onSignedIn?.();
        onDone?.();
      }}
      onDone={() => {
        navigate('/', { replace: true });
        onDone?.();
      }}
    />
  );
}
